import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash, User, Phone, ShieldCheck, AddressBook, PencilSimple, Key, Copy } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function NasStaff() {
  const [nasList, setNasList] = useState([]);
  const [nasId, setNasId] = useState("");
  const [staff, setStaff] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [tab, setTab] = useState("staff");
  const [matrix, setMatrix] = useState([]);
  useEffect(() => {
    if (tab !== "matrix") return;
    (async () => {
      try { setMatrix((await http.get("/nas-staff/all")).data || []); } catch { setMatrix([]); }
    })();
  }, [tab]);
  const [rolesList, setRolesList] = useState([]);
  const [selected, setSelected] = useState({}); // {staff_id: true}
  const [bulkRole, setBulkRole] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const bulkAssign = async () => {
    if (!selectedIds.length) return;
    const roleName = bulkRole ? (rolesList.find((r) => r.id === bulkRole)?.name || bulkRole) : "No role";
    if (!confirm(`Assign "${roleName}" to ${selectedIds.length} selected employee(s)?`)) return;
    try {
      setBulkSaving(true);
      const { data } = await http.post("/nas-staff/bulk/assign-role", { staff_ids: selectedIds, role_id: bulkRole || null });
      toast.success(`Assigned: ${data.modified}/${data.matched} employees`);
      setSelected({}); setBulkRole(""); loadStaffContacts();
    } catch (err) { toast.error(err?.response?.data?.detail || "Bulk assign failed"); }
    finally { setBulkSaving(false); }
  };

  const [openStaff, setOpenStaff] = useState(false);
  const [openContact, setOpenContact] = useState(false);
  const [editStaff, setEditStaff] = useState(null); // {id, name, email, phone, designation, role, role_id, password}
  const [staffForm, setStaffForm] = useState({ name: "", email: "", phone: "", designation: "Field Engineer", role: "engineer", role_id: "", password: "" });
  const [contactForm, setContactForm] = useState({ name: "", phone: "", designation: "", notes: "" });
  const saveStaffEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editStaff }; if (!payload.password) delete payload.password;
      await http.patch(`/nas/${nasId}/staff/${editStaff.id}`, payload);
      toast.success("Staff updated"); setEditStaff(null); loadStaffContacts();
    } catch (err) { toast.error(err?.response?.data?.detail || "Update failed"); }
  };

  const loadNas = async () => {
    const [{ data: nas }, { data: rls }] = await Promise.all([http.get("/nas"), http.get("/roles").catch(() => ({ data: [] }))]);
    setNasList(nas);
    setRolesList(rls || []);
    if (!nasId && nas.length) setNasId(nas[0].id);
  };
  const loadStaffContacts = async () => {
    if (!nasId) return;
    const [a, b] = await Promise.all([http.get(`/nas/${nasId}/staff`), http.get(`/nas/${nasId}/contacts`)]);
    setStaff(a.data); setContacts(b.data);
  };
  useEffect(() => { loadNas(); }, []);
  useEffect(() => { loadStaffContacts(); }, [nasId]);

  const createStaff = async (e) => {
    e.preventDefault();
    try {
      await http.post(`/nas/${nasId}/staff`, { ...staffForm, nas_id: nasId, email: staffForm.email.toLowerCase() });
      toast.success("Staff account created");
      setOpenStaff(false);
      setStaffForm({ name: "", email: "", phone: "", designation: "Field Engineer", role: "engineer", password: "" });
      loadStaffContacts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create staff");
    }
  };
  const removeStaff = async (id) => {
    if (!confirm("Remove this staff account?")) return;
    await http.delete(`/nas/${nasId}/staff/${id}`); toast.success("Removed"); loadStaffContacts();
  };

  const createContact = async (e) => {
    e.preventDefault();
    await http.post(`/nas/${nasId}/contacts`, { ...contactForm, nas_id: nasId });
    toast.success("Contact added");
    setOpenContact(false);
    setContactForm({ name: "", phone: "", designation: "", notes: "" });
    loadStaffContacts();
  };
  const removeContact = async (id) => {
    if (!confirm("Remove this contact?")) return;
    await http.delete(`/nas/${nasId}/contacts/${id}`); loadStaffContacts();
  };

  const nas = nasList.find((n) => n.id === nasId);

  return (
    <div className="space-y-6 fade-up" data-testid="nas-staff-view">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="uppercase-label">NAS user management</div>
          <h1 className="font-display font-bold text-4xl mt-1">Employees under NAS</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Login accounts (Staff) or on-call contact list per RADIUS client. Staff can sign in at <code>/staff/login</code> scoped to their NAS.</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={nasId} onValueChange={setNasId}>
            <SelectTrigger className="w-64" data-testid="ns-nas-select"><SelectValue placeholder="Select NAS" /></SelectTrigger>
            <SelectContent>{nasList.map((n) => <SelectItem key={n.id} value={n.id}>{n.name} — {n.nas_ip}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {nas && (
        <div className="border border-border bg-card p-4 text-sm flex items-center gap-6 flex-wrap font-mono">
          <span><span className="uppercase-label">NAS</span> <span className="ml-2">{nas.name}</span></span>
          <span><span className="uppercase-label">IP</span> <span className="ml-2">{nas.nas_ip}</span></span>
          <span><span className="uppercase-label">Type</span> <span className="ml-2">{nas.nas_type}</span></span>
        </div>
      )}

      <div className="flex items-center gap-2">
        {[
          { k: "staff", label: "Staff logins", icon: ShieldCheck, count: staff.length },
          { k: "contacts", label: "Contact list", icon: AddressBook, count: contacts.length },
          { k: "matrix", label: "By NAS", icon: AddressBook, count: nasList.length },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`ns-tab-${t.k}`}
            className={`inline-flex items-center gap-2 text-xs uppercase tracking-wide px-3 py-1.5 border rounded-sm transition-colors ${tab === t.k ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground"}`}>
            <t.icon size={12} /> {t.label} <span className="font-mono text-[10px] opacity-70">({t.count})</span>
          </button>
        ))}
        <div className="ml-auto">
          {tab === "staff" ? (
            <Dialog open={openStaff} onOpenChange={setOpenStaff}>
              <DialogTrigger asChild>
                <Button data-testid="new-staff-btn" className="rounded-sm bg-primary hover:bg-primary/90" disabled={!nasId}><Plus size={14} className="mr-2" weight="bold" /> New staff login</Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border max-w-lg">
                <DialogHeader><DialogTitle className="font-display">New staff account</DialogTitle></DialogHeader>
                <form onSubmit={createStaff} className="grid grid-cols-2 gap-4">
                  <F label="Full name" span={2}><Input required data-testid="staff-name" value={staffForm.name} onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })} /></F>
                  <F label="Email"><Input required type="email" data-testid="staff-email" value={staffForm.email} onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })} /></F>
                  <F label="Phone"><Input data-testid="staff-phone" value={staffForm.phone} onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })} /></F>
                  <F label="Designation"><Input data-testid="staff-desig" value={staffForm.designation} onChange={(e) => setStaffForm({ ...staffForm, designation: e.target.value })} /></F>
                  <F label="Role">
                    <Select value={staffForm.role} onValueChange={(v) => setStaffForm({ ...staffForm, role: v })}>
                      <SelectTrigger data-testid="staff-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="viewer">Viewer</SelectItem>
                        <SelectItem value="engineer">Engineer</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                      </SelectContent>
                    </Select>
                  </F>
                  <F label="Permission Role" span={2}>
                    <Select value={staffForm.role_id || "__none__"} onValueChange={(v) => setStaffForm({ ...staffForm, role_id: v === "__none__" ? "" : v })}>
                      <SelectTrigger data-testid="staff-role-id"><SelectValue placeholder="No role — configure permissions individually" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">No role — configure per-employee</SelectItem>
                        {rolesList.map((r) => (
                          <SelectItem key={r.id} value={r.id}>{r.name} · {(r.permissions || []).length} perms</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </F>
                  <F label="Password" span={2}><Input required type="text" data-testid="staff-pass" value={staffForm.password} onChange={(e) => setStaffForm({ ...staffForm, password: e.target.value })} className="font-mono" /></F>
                  <DialogFooter className="col-span-2"><Button type="submit" data-testid="staff-submit" className="rounded-sm bg-primary hover:bg-primary/90">Create</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          ) : (
            <Dialog open={openContact} onOpenChange={setOpenContact}>
              <DialogTrigger asChild>
                <Button data-testid="new-contact-btn" className="rounded-sm bg-primary hover:bg-primary/90" disabled={!nasId}><Plus size={14} className="mr-2" weight="bold" /> Add contact</Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border max-w-md">
                <DialogHeader><DialogTitle className="font-display">Add contact (no login)</DialogTitle></DialogHeader>
                <form onSubmit={createContact} className="space-y-4">
                  <F label="Name"><Input required data-testid="contact-name" value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} /></F>
                  <F label="Phone"><Input required data-testid="contact-phone" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} /></F>
                  <F label="Designation"><Input data-testid="contact-desig" value={contactForm.designation} onChange={(e) => setContactForm({ ...contactForm, designation: e.target.value })} /></F>
                  <F label="Notes"><Textarea rows={2} data-testid="contact-notes" value={contactForm.notes} onChange={(e) => setContactForm({ ...contactForm, notes: e.target.value })} /></F>
                  <DialogFooter><Button type="submit" data-testid="contact-submit" className="rounded-sm bg-primary hover:bg-primary/90">Add</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {tab === "staff" && selectedIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 border border-primary/40 bg-primary/5 px-4 py-2 rounded-sm" data-testid="bulk-toolbar">
          <span className="text-xs font-mono">{selectedIds.length} selected</span>
          <Select value={bulkRole || "__none__"} onValueChange={(v) => setBulkRole(v === "__none__" ? "" : v)}>
            <SelectTrigger className="h-8 w-64 text-xs" data-testid="bulk-role-select"><SelectValue placeholder="Pick role to assign…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">No role — clear assignment</SelectItem>
              {rolesList.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} · {(r.permissions || []).length} perms</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={bulkAssign} disabled={bulkSaving} className="rounded-sm h-8 text-xs" data-testid="bulk-assign-apply">
            {bulkSaving ? "Applying…" : "Apply"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setSelected({})} className="rounded-sm h-8 text-xs" data-testid="bulk-clear">Clear</Button>
        </div>
      )}

      {tab === "staff" ? (
        <div className="border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-muted-foreground">
              <th className="uppercase-label text-left py-2 px-3 w-8"></th>
              <th className="uppercase-label text-left py-2 px-3">Name</th>
              <th className="uppercase-label text-left py-2 px-3">Email</th>
              <th className="uppercase-label text-left py-2 px-3 hidden md:table-cell">Phone</th>
              <th className="uppercase-label text-left py-2 px-3 hidden md:table-cell">Designation</th>
              <th className="uppercase-label text-left py-2 px-3">Role</th>
              <th className="uppercase-label text-right py-2 px-3">Action</th>
            </tr></thead>
            <tbody>
              {staff.map((s) => (
                <tr key={s.id} className="border-b border-border/60 hover:bg-accent/30" data-testid={`staff-row-${s.id}`}>
                  <td className="py-2 px-3">
                    <input
                      type="checkbox"
                      checked={!!selected[s.id]}
                      onChange={(e) => setSelected((prev) => ({ ...prev, [s.id]: e.target.checked }))}
                      data-testid={`select-staff-${s.id}`}
                    />
                  </td>
                  <td className="py-2 px-3 flex items-center gap-2"><User size={14} className="text-muted-foreground" /> {s.name}</td>
                  <td className="py-2 px-3 data-cell text-muted-foreground">{s.email}</td>
                  <td className="py-2 px-3 data-cell hidden md:table-cell">{s.phone || "—"}</td>
                  <td className="py-2 px-3 hidden md:table-cell">{s.designation}</td>
                  <td className="py-2 px-3"><span className="text-xs uppercase font-mono px-2 py-0.5 border border-primary/40 text-primary bg-primary/10 rounded-sm">{s.role}</span></td>
                  <td className="py-2 px-3 text-right">
                    <div className="inline-flex items-center gap-3">
                      <Link
                        to={`/nas/${nasId}/staff/${s.id}/permissions`}
                        state={{ staffName: s.name, nasId }}
                        className="text-muted-foreground hover:text-primary"
                        title="Employee permissions"
                        data-testid={`staff-perms-${s.id}`}
                      ><Key size={14} /></Link>
                      <button
                        onClick={() => setEditStaff({ id: s.id, name: s.name, email: s.email, phone: s.phone || "", designation: s.designation || "", role: s.role, role_id: s.role_id || "", password: "" })}
                        className="text-muted-foreground hover:text-primary"
                        title="Edit staff"
                        data-testid={`staff-edit-${s.id}`}
                      ><PencilSimple size={14} /></button>
                      <button onClick={() => removeStaff(s.id)} className="text-muted-foreground hover:text-destructive" title="Remove staff" data-testid={`staff-del-${s.id}`}><Trash size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {staff.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-muted-foreground text-sm">No staff accounts under this NAS.</td></tr>}
            </tbody>
          </table>
        </div>
      ) : tab === "matrix" ? (
        <div className="space-y-4" data-testid="matrix-view">
          {matrix.map((row) => (
            <div key={row.nas.id} className="border border-border bg-card">
              <div className="p-3 border-b border-border flex items-center justify-between">
                <div>
                  <div className="font-display font-bold text-sm">{row.nas.name}</div>
                  <div className="uppercase-label mt-0.5 font-mono">{row.nas.nas_ip} · {row.nas.location || "—"}</div>
                </div>
                <span className="text-xs font-mono px-2 py-0.5 border border-primary/40 text-primary bg-primary/10">{row.employees_count} employees</span>
              </div>
              {row.staff.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground text-xs italic">No staff assigned to this NAS.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-border text-muted-foreground"><th className="uppercase-label text-left py-2 px-3">Name</th><th className="uppercase-label text-left py-2 px-3">Email</th><th className="uppercase-label text-left py-2 px-3">Role</th><th className="uppercase-label text-left py-2 px-3 hidden md:table-cell">Designation</th></tr></thead>
                  <tbody>{row.staff.map((s) => (
                    <tr key={s.id} className="border-b border-border/60"><td className="py-1.5 px-3">{s.name}</td><td className="py-1.5 px-3 font-mono text-muted-foreground">{s.email}</td><td className="py-1.5 px-3"><span className="text-[10px] uppercase font-mono px-1.5 py-0.5 border border-border">{s.role}</span></td><td className="py-1.5 px-3 hidden md:table-cell">{s.designation || "—"}</td></tr>
                  ))}</tbody>
                </table>
              )}
            </div>
          ))}
          {matrix.length === 0 && <div className="text-center py-10 text-muted-foreground text-sm border border-dashed border-border">Loading matrix…</div>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="contacts-view">
          {contacts.map((c) => (
            <div key={c.id} className="border border-border bg-card p-4" data-testid={`contact-card-${c.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-lg font-bold">{c.name}</div>
                  <div className="uppercase-label mt-0.5">{c.designation || "Contact"}</div>
                </div>
                <button onClick={() => removeContact(c.id)} className="text-muted-foreground hover:text-destructive"><Trash size={14} /></button>
              </div>
              <div className="mt-3 flex items-center gap-2 text-sm font-mono"><Phone size={14} className="text-primary" /> {c.phone}</div>
              {c.notes && <div className="text-xs text-muted-foreground mt-3 border-t border-border pt-2">{c.notes}</div>}
            </div>
          ))}
          {contacts.length === 0 && <div className="col-span-full text-center py-10 text-muted-foreground text-sm border border-dashed border-border">No contacts yet.</div>}
        </div>
      )}
{/* matrix rendered above via ternary */}

      {/* Edit staff dialog */}
      <Dialog open={!!editStaff} onOpenChange={(v) => !v && setEditStaff(null)}>
        <DialogContent className="bg-card border-border max-w-lg" data-testid="staff-edit-modal">
          <DialogHeader><DialogTitle className="font-display">Edit staff — {editStaff?.name}</DialogTitle></DialogHeader>
          {editStaff && (
            <form onSubmit={saveStaffEdit} className="grid grid-cols-2 gap-4">
              <F label="Full name" span={2}><Input required data-testid="edit-name" value={editStaff.name} onChange={(e) => setEditStaff({ ...editStaff, name: e.target.value })} /></F>
              <F label="Email"><Input required type="email" data-testid="edit-email" value={editStaff.email} onChange={(e) => setEditStaff({ ...editStaff, email: e.target.value })} /></F>
              <F label="Phone"><Input data-testid="edit-phone" value={editStaff.phone || ""} onChange={(e) => setEditStaff({ ...editStaff, phone: e.target.value })} /></F>
              <F label="Designation"><Input data-testid="edit-desig" value={editStaff.designation || ""} onChange={(e) => setEditStaff({ ...editStaff, designation: e.target.value })} /></F>
              <F label="Role">
                <Select value={editStaff.role} onValueChange={(v) => setEditStaff({ ...editStaff, role: v })}>
                  <SelectTrigger data-testid="edit-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viewer">Viewer</SelectItem>
                    <SelectItem value="engineer">Engineer</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                  </SelectContent>
                </Select>
              </F>
              <F label="Permission Role" span={2}>
                <Select value={editStaff.role_id || "__none__"} onValueChange={(v) => setEditStaff({ ...editStaff, role_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="edit-role-id"><SelectValue placeholder="No role" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No role — configure per-employee</SelectItem>
                    {rolesList.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.name} · {(r.permissions || []).length} perms</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </F>
              <F label="New password (blank = keep)" span={2}><Input type="text" data-testid="edit-pass" value={editStaff.password} onChange={(e) => setEditStaff({ ...editStaff, password: e.target.value })} className="font-mono" placeholder="leave empty to keep current" /></F>
              <DialogFooter className="col-span-2"><Button type="submit" data-testid="edit-submit" className="rounded-sm bg-primary hover:bg-primary/90">Save</Button></DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({ label, span = 1, children }) => (
  <div className={span === 2 ? "col-span-2" : ""}>
    <Label className="uppercase-label">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
